import { isAfter } from 'date-fns'
import * as Yup from 'yup'

import {
  EMPTY_LIST,
  EMPTY_STRING,
  NOTIFICATION_TYPE,
} from '../../../../constants/common'
import { REG_EXP_WYSIWYG_HTML_START } from '../../../../constants/regExp'
import {
  FORMIK_EMPTY_VALUE,
  SERVER_NON_FIELD_ERROR_KEY,
} from '../../../../constants/validation'
import {
  hasTinyMceHtmlVisibleContent,
  newLineToBrInHtml,
  plainTextToHtml,
} from '../../../../utils/html'
import { parseAmount } from '../../../../utils/number'
import { hasFormikFormEmptyRequired } from '../../../Formik'
import {
  getIsListingAmountWithinMax,
  getIsListingPublicationInProgressError,
  getListingAmountMaxErrorText,
  LISTING_FORM_FIELD_NAME_AVAILABLE_FROM,
  LISTING_FORM_FIELD_NAME_DEPOSIT_AMOUNT,
  LISTING_FORM_FIELD_NAME_DEPOSIT_TEXT,
  LISTING_FORM_FIELD_NAME_DESCRIPTION,
  LISTING_FORM_FIELD_NAME_FLAT,
  LISTING_FORM_FIELD_NAME_PHOTOS,
  LISTING_FORM_FIELD_NAME_PORTALS,
  LISTING_FORM_FIELD_NAME_RENT_PRICE,
  LISTING_FORM_FIELD_NAME_SCHEDULED_DEACTIVATE,
  LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE,
  LISTING_FORM_FIELD_NAME_SERVICE_CHARGE,
  LISTING_PUBLICATION_IN_PROGRESS_ERROR_KEY,
  LISTING_READINESS_LABEL_KEY,
  toListingFlatSelectItems,
} from '../../../RentalPortalListing'
import {
  getListingDateValue,
  parseListingDayInAppTimezone,
  parseListingFormDate,
} from '../../RentalPortal.utils'
import {
  getHasChangedPhotos,
  toPhotoFormValues,
} from '../../RentalPortalPhotoGallery'

import {
  LISTING_FORM_AD_MATERIAL_FIELD_NAMES,
  LISTING_FORM_FIELD_NAME_TIMING,
  LISTING_FORM_INITIAL_VALUES,
  LISTING_FORM_TIMING,
} from './RentalPortalListingsForm.constants'

function getScheduleValidationSchema(t) {
  return {
    [LISTING_FORM_FIELD_NAME_SCHEDULED_DEACTIVATE]: Yup.date()
      .nullable()
      .test({
        message: t('RentalPortal.ListingFormDeactivateInPastKey'),
        name: 'deactivateInFuture',
        test(value) {
          return (
            !value ||
            !getIsListingFormScheduled(this.parent) ||
            isAfter(value, new Date())
          )
        },
      })
      .test({
        message: t('RentalPortal.ListingFormDeactivateBeforeGoLiveKey'),
        name: 'deactivateAfterGoLive',
        test(value) {
          const goLiveValue =
            this.parent[LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE]

          return (
            !value ||
            !goLiveValue ||
            !getIsListingFormScheduled(this.parent) ||
            isAfter(value, goLiveValue)
          )
        },
      }),
    [LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE]: Yup.date()
      .nullable()
      .test({
        message: t('RentalPortal.ListingFormGoLiveInPastKey'),
        name: 'goLiveInFuture',
        test(value) {
          return (
            !value ||
            !getIsListingFormScheduled(this.parent) ||
            isAfter(value, new Date())
          )
        },
      }),
  }
}

function getListingCostValidationSchema(t) {
  return Yup.string()
    .ensure()
    .test(
      'nonNegative',
      t('Error.EnterPositiveNumberKey'),
      value => !value || parseAmount(value) >= 0
    )
    .test('max', getListingAmountMaxErrorText(t), getIsListingAmountWithinMax)
}

function getListingFormDescriptionInitialValue(description) {
  return REG_EXP_WYSIWYG_HTML_START.test(description || FORMIK_EMPTY_VALUE)
    ? newLineToBrInHtml(description)
    : plainTextToHtml(description)
}

function getListingFormAmountValue(value) {
  return value == null ? FORMIK_EMPTY_VALUE : String(value)
}

function getListingFormListingValues({ listing, t, timezone }) {
  return {
    ...LISTING_FORM_INITIAL_VALUES,
    [LISTING_FORM_FIELD_NAME_AVAILABLE_FROM]: parseListingDayInAppTimezone(
      listing.available_from,
      timezone
    ),
    [LISTING_FORM_FIELD_NAME_DEPOSIT_AMOUNT]: getListingFormAmountValue(
      listing.deposit_amount
    ),
    [LISTING_FORM_FIELD_NAME_DEPOSIT_TEXT]:
      listing.deposit_text ?? FORMIK_EMPTY_VALUE,
    [LISTING_FORM_FIELD_NAME_DESCRIPTION]:
      getListingFormDescriptionInitialValue(listing.description),
    [LISTING_FORM_FIELD_NAME_FLAT]: listing.flat
      ? toListingFlatSelectItems({ items: [listing.flat], t })[0]
      : null,
    [LISTING_FORM_FIELD_NAME_PHOTOS]: toPhotoFormValues(listing.photos),
    [LISTING_FORM_FIELD_NAME_RENT_PRICE]: getListingFormAmountValue(
      listing.rent_price
    ),
    [LISTING_FORM_FIELD_NAME_SCHEDULED_DEACTIVATE]: parseListingFormDate(
      listing.scheduled_deactivate,
      timezone
    ),
    [LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE]: parseListingFormDate(
      listing.scheduled_go_live,
      timezone
    ),
    [LISTING_FORM_FIELD_NAME_SERVICE_CHARGE]: getListingFormAmountValue(
      listing.service_charge
    ),
  }
}

function getListingAmountPayloadValue(value) {
  return value ? parseAmount(value) : null
}

export function getIsListingDraftTiming(timing) {
  return timing === LISTING_FORM_TIMING.draft
}

export function getIsListingFormScheduled(parent) {
  return !getIsListingDraftTiming(parent[LISTING_FORM_FIELD_NAME_TIMING])
}

export function getValidationSchema({ listingId, t }) {
  return Yup.object({
    ...(getHasListingPublishSection({ listingId })
      ? getScheduleValidationSchema(t)
      : null),
    [LISTING_FORM_FIELD_NAME_DEPOSIT_AMOUNT]: getListingCostValidationSchema(t),
    [LISTING_FORM_FIELD_NAME_DEPOSIT_TEXT]: Yup.string().ensure(),
    [LISTING_FORM_FIELD_NAME_DESCRIPTION]: Yup.string().ensure(),
    [LISTING_FORM_FIELD_NAME_FLAT]: Yup.object().nullable().required(true),
    [LISTING_FORM_FIELD_NAME_RENT_PRICE]: Yup.string()
      .ensure()
      .test(
        'positive',
        t('RentalPortal.ListingRentPositiveErrorKey'),
        value => !value || parseAmount(value) > 0
      )
      .test(
        'max',
        getListingAmountMaxErrorText(t),
        getIsListingAmountWithinMax
      ),
    [LISTING_FORM_FIELD_NAME_SERVICE_CHARGE]: getListingCostValidationSchema(t),
  })
}

export function getHasListingDisconnectedPortals({
  hasPortalsError,
  isLoadingPortals,
  portals,
  selectedPortals,
}) {
  if (isLoadingPortals || hasPortalsError) {
    return false
  }

  return selectedPortals.some(
    name => !portals.some(portal => portal.portal === name && portal.connected)
  )
}

export function checkListingFormForEmptyRequired(errors) {
  return hasFormikFormEmptyRequired(errors)
}

export function getListingFormInitialValues({
  draftValues,
  listing,
  t,
  timezone,
}) {
  if (draftValues) {
    return {
      ...LISTING_FORM_INITIAL_VALUES,
      ...draftValues,
      [LISTING_FORM_FIELD_NAME_AVAILABLE_FROM]: parseListingFormDate(
        draftValues[LISTING_FORM_FIELD_NAME_AVAILABLE_FROM],
        timezone
      ),
      [LISTING_FORM_FIELD_NAME_SCHEDULED_DEACTIVATE]: parseListingFormDate(
        draftValues[LISTING_FORM_FIELD_NAME_SCHEDULED_DEACTIVATE],
        timezone
      ),
      [LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE]: parseListingFormDate(
        draftValues[LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE],
        timezone
      ),
    }
  }

  return listing
    ? getListingFormListingValues({ listing, t, timezone })
    : LISTING_FORM_INITIAL_VALUES
}

export function getPayload(values) {
  return {
    available_from: getListingDateValue(
      values[LISTING_FORM_FIELD_NAME_AVAILABLE_FROM]
    ),
    deposit_amount: getListingAmountPayloadValue(
      values[LISTING_FORM_FIELD_NAME_DEPOSIT_AMOUNT]
    ),
    deposit_text: values[LISTING_FORM_FIELD_NAME_DEPOSIT_TEXT],
    description: values[LISTING_FORM_FIELD_NAME_DESCRIPTION],
    flat_id: values[LISTING_FORM_FIELD_NAME_FLAT].value,
    rent_price: getListingAmountPayloadValue(
      values[LISTING_FORM_FIELD_NAME_RENT_PRICE]
    ),
    service_charge: getListingAmountPayloadValue(
      values[LISTING_FORM_FIELD_NAME_SERVICE_CHARGE]
    ),
  }
}

export function getHasListingPortalsToPublish({ listingId, timing, values }) {
  return (
    getHasListingPublishSection({ listingId }) &&
    values[LISTING_FORM_FIELD_NAME_PORTALS].length > 0 &&
    !getIsListingDraftTiming(timing)
  )
}

export function getListingScheduleValue(value) {
  return value ? new Date(value).toISOString() : null
}

export function getListingPortalsPublishPayload(values) {
  return {
    deactivate_at: getListingScheduleValue(
      values[LISTING_FORM_FIELD_NAME_SCHEDULED_DEACTIVATE]
    ),
    portals: values[LISTING_FORM_FIELD_NAME_PORTALS],
    publish_at: getListingScheduleValue(
      values[LISTING_FORM_FIELD_NAME_SCHEDULED_GO_LIVE]
    ),
  }
}

export function getHasListingPublishSection({ listingId }) {
  return !listingId
}

export function getListingFormMissingPublishItems({ t, values }) {
  return [
    values[LISTING_FORM_FIELD_NAME_RENT_PRICE]
      ? null
      : LISTING_READINESS_LABEL_KEY.rent,
    hasTinyMceHtmlVisibleContent(values[LISTING_FORM_FIELD_NAME_DESCRIPTION])
      ? null
      : LISTING_READINESS_LABEL_KEY.description,
    (values[LISTING_FORM_FIELD_NAME_PHOTOS] ?? EMPTY_LIST).length > 0
      ? null
      : LISTING_READINESS_LABEL_KEY.photo,
  ]
    .filter(Boolean)
    .map(labelKey => t(labelKey))
}

export function getListingTimingErrorText({ error, t }) {
  return t(
    getIsListingPublicationInProgressError(error)
      ? LISTING_PUBLICATION_IN_PROGRESS_ERROR_KEY
      : 'RentalPortal.ListingFormScheduleErrorKey'
  )
}

export function setListingFormSubmitError({
  errorText,
  setErrors,
  setSubmitting,
}) {
  setSubmitting(false)

  setErrors({
    [SERVER_NON_FIELD_ERROR_KEY]: errorText,
  })
}

export function getHasChangedListingAdMaterial({ initialValues, values }) {
  return (
    LISTING_FORM_AD_MATERIAL_FIELD_NAMES.some(
      name =>
        String(values[name] ?? EMPTY_STRING) !==
        String(initialValues[name] ?? EMPTY_STRING)
    ) ||
    getHasChangedPhotos({
      initialPhotos: initialValues[LISTING_FORM_FIELD_NAME_PHOTOS],
      photos: values[LISTING_FORM_FIELD_NAME_PHOTOS],
    })
  )
}

export function getListingFormPublishNotifications({
  blockReason,
  notifications,
}) {
  return blockReason
    ? [
        ...notifications,
        {
          id: 'listingFormPublishBlocked',
          text: blockReason,
          type: NOTIFICATION_TYPE.warning,
        },
      ]
    : notifications
}

export function getIsListingUnitLocked({ isEditMode }) {
  return isEditMode
}

export function getIsListingFormReadOnly({ isEditMode, listing }) {
  return isEditMode && Boolean(listing) && !listing.permissions?.can_edit
}

export function getListingFormNotifications({
  loadError,
  notifications,
  retryIconProps,
  t,
  onRetryLoad,
}) {
  return loadError
    ? [
        ...notifications,
        {
          actions: [
            {
              beforeIconProps: retryIconProps,
              callback: onRetryLoad,
              text: t('RentalPortal.ListingRetryBtnKey'),
            },
          ],
          id: 'listingFormLoadError',
          text: t('RentalPortal.ListingFormLoadErrorKey'),
          type: NOTIFICATION_TYPE.error,
        },
      ]
    : notifications
}
